import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useMutation } from '@tanstack/react-query';
import Routes from '../../utils/routes.js.erb';
import { authHeaders } from '../../actions';

const PlayerSelector = React.memo(({
  value,
  onChange,
  isDisabled,
  onErrorMessage,
  isLoading = false,
}) => {
  const searchTimeoutRef = useRef(null);

  const loadOptions = (inputValue, callback) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!inputValue.trim()) {
      callback([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPlayersMutation.mutate(inputValue, {
        onSuccess: (data) => {
          callback(data);
        },
        onError: (error) => {
          onErrorMessage('Oops! Something went wrong. Contact support for help.');
          console.error('Error fetching players:', error);
          callback([]);
        }
      });
    }, 500);
  };

  const searchPlayersMutation = useMutation({
    mutationFn: async (searchQuery) => {
      const response = await fetch(
        Routes.api_dashboard_communication_filters_for_players_path({ q: searchQuery }),
        {
          method: 'GET',
          headers: authHeaders()
        }
      );

      if (!response.ok) {
        throw new Error('Error fetching players');
      }

      return response.json();
    }
  });

  return (
    <AsyncSelect
      name='sp-player-select'
      className='o-select--optgroup'
      isMulti={true}
      value={value}
      loadOptions={loadOptions}
      placeholder={isLoading ? 'Loading...' : 'Start typing'}
      onChange={onChange}
      isDisabled={isDisabled || isLoading}
      cacheOptions={true}
      defaultOptions={false}
    />
  );
});

const Recipients = ({
  filters,
  isLoadingGroups = true,
  onAgeChange,
  onRecipientsChange,
  onErrorMessage,
  forGroups = false,
  selectedIndividuals = [],
}) => {
  const [sourceGroups, setSourceGroups] = useState([]);
  const [targetAges, setTargetAges] = useState([]);
  const [targetGroups, setTargetGroups] = useState([]);
  const [targetPlayers, setTargetPlayers] = useState(selectedIndividuals);

  useEffect(() => {
    setTargetPlayers(selectedIndividuals);
  }, [selectedIndividuals]);

  useEffect(() => {
    const value = [
      {
        label: 'Groups',
        options: filters.groups?.map(g => ({ value: `group_${g.value}`, label: g.label })) || [],
      },
      {
        label: 'Subgroups',
        options: filters.subgroups?.map(g => ({ value: `subgroup_${g.value}`, label: g.label })) || [],
      },
      {
        label: 'Cohorts',
        options: filters.cohorts?.map(g => ({ value: `cohort_${g.value}`, label: g.label })) || [],
      }
    ]
    setSourceGroups(value);
  }, [filters])

  const onIndividualsChange = (value) => {
    setTargetPlayers(value);
    onRecipientsChange(value);
  }

  const onGroupChange = (value) => {
    setTargetGroups(value);
    onRecipientsChange(value);
  }

  const onAgesChange = (value) => {
    setTargetAges(value);
    onAgeChange(value);
  }

  const renderIndividuals = () => {
    return (
      <div className="column u-padding-right--zero">
        <PlayerSelector
          value={targetPlayers}
          onChange={onIndividualsChange}
          isDisabled={isLoadingGroups}
          isLoading={isLoadingGroups}
          onErrorMessage={onErrorMessage}
        />
      </div>
    )
  };

  const renderGroups = () => {
    return (
      <div className="row u-padding-right--zero u-padding-left--base medium-12">
        <div className="column medium-8">
          <Select
            name="sp-group-select"
            className={'o-select--optgroup'}
            isMulti={true}
            value={targetGroups}
            onChange={onGroupChange}
            options={sourceGroups}
            placeholder="Start typing"
            isDisabled={isLoadingGroups}/>
        </div>
        {renderAgeRanges()}
      </div>
    )
  };

  const renderAgeRanges = () => {
    return (
      <div className="column u-padding-left--zero u-padding-right--zero">
        <Select
          name="sp-age-select"
          className={'o-select--optgroup'}
          isMulti={true}
          value={targetAges}
          onChange={onAgesChange}
          options={filters.ages || []}
          placeholder="Age Ranges"
          isDisabled={isLoadingGroups}/>
      </div>
    )
  }

  return (
    <div className="row u-margin-bottom--tiny" style={{alignItems: 'first baseline'}}>
      <label>To:</label>
      {forGroups ? renderGroups() : renderIndividuals()}
    </div>
  )
}

Recipients.propTypes = {
  filters: PropTypes.shape({
    ages: PropTypes.array,
    cohorts: PropTypes.array,
    groups: PropTypes.array,
    subgroups: PropTypes.array,
  }),
  isLoadingGroups: PropTypes.bool.isRequired,
  forGroups: PropTypes.bool,
  onAgeChange: PropTypes.func.isRequired,
  onRecipientsChange: PropTypes.func.isRequired,
  selectedIndividuals: PropTypes.array,
  onErrorMessage: PropTypes.func.isRequired,
}

export default Recipients;
